export interface ICognitoStorage {
  setItem(key: string, value: string): void;
  getItem(key: string): string | null;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * Class that implements ICognitoStorage using localStorage for daily operations,
 * but provides methods to temporarily sync to cookies for cross-subdomain redirection.
 * This prevents the '431 Request Header Fields Too Large' error caused by huge cookies.
 */
export class CognitoStorageService implements ICognitoStorage {
  /**
   * Initializes the storage service.
   * On instantiation, it immediately attempts to read any cross-domain cookies
   * that may have been set during a subdomain redirect, moving those tokens safely
   * into localStorage and cleaning up the cookies.
   */
  constructor() {
    CognitoStorageService.syncFromCookies();
  }

  /**
   * Calculates the root domain for the current environment to ensure cookies
   * are scoped globally and can be read across subdomains (e.g. prep-asis vs prep-cenwood).
   * For local development (localhost), it returns an empty string to prevent cookie rejection.
   *
   * @returns {string} The formatted domain string to be appended to the cookie, or an empty string.
   */
  private static getDomain(): string {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || /^[0-9.]+$/.test(hostname)) {
      return '';
    }
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return `domain=.${parts.slice(-2).join('.')};`;
    }
    return `domain=.${hostname};`;
  }

  /**
   * Stores an item in local storage.
   *
   * @param {string} key - The key under which to store the value.
   * @param {string} value - The stringified token or data to store.
   */
  setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  /**
   * Retrieves an item from local storage by its key.
   *
   * @param {string} key - The key of the item to retrieve.
   * @returns {string | null} The retrieved value, or null if it doesn't exist.
   */
  getItem(key: string): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Removes a specific item from local storage.
   *
   * @param {string} key - The key of the item to remove.
   */
  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Clears all Cognito and Amplify related authentication data from local storage.
   * It specifically avoids clearing other unrelated application data by checking
   * for the 'CognitoIdentityServiceProvider' and 'amplify' prefixes.
   */
  clear(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('CognitoIdentityServiceProvider') ||
          key.startsWith('amplify'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  }

  /**
   * Temporarily writes the Cognito session from localStorage to cross-domain cookies.
   * Call this IMMEDIATELY before a window.location redirect.
   */
  public static syncToCookies(): void {
    const domainStr = this.getDomain();
    const secureStr = window.location.protocol === 'https:' ? 'secure;' : '';
    const CHUNK_SIZE = 3000;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('CognitoIdentityServiceProvider') ||
          key.startsWith('amplify') ||
          key.includes('selectedLob') ||
          key.includes('selectedWarehouse') ||
          key.includes('language'))
      ) {
        const value = localStorage.getItem(key) || '';
        const encodedKey = encodeURIComponent(key);
        const encodedValue = encodeURIComponent(value);

        if (encodedValue.length <= CHUNK_SIZE) {
          document.cookie = `${encodedKey}=${encodedValue};${domainStr}path=/;${secureStr}samesite=lax;max-age=60`;
        } else {
          const numChunks = Math.ceil(encodedValue.length / CHUNK_SIZE);
          for (let j = 0; j < numChunks; j++) {
            const chunk = encodedValue.substring(
              j * CHUNK_SIZE,
              (j + 1) * CHUNK_SIZE,
            );
            document.cookie = `${encodedKey}.chunk${j}=${chunk};${domainStr}path=/;${secureStr}samesite=lax;max-age=60`;
          }
          document.cookie = `${encodedKey}=chunks:${numChunks};${domainStr}path=/;${secureStr}samesite=lax;max-age=60`;
        }
      }
    }
  }

  /**
   * Reads the cross-domain cookies into localStorage and deletes the cookies.
   * This is called automatically when the app initializes.
   */
  public static syncFromCookies(): void {
    const domainStr = CognitoStorageService.getDomain();
    const cookies = document.cookie.split(';');
    const processedKeys = new Set<string>();

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      if (eqPos === -1) continue;

      let name = cookie.substr(0, eqPos).trim();
      const isChunk = name.includes('.chunk');
      if (isChunk) {
        name = name.substring(0, name.indexOf('.chunk'));
      }

      const decodedName = decodeURIComponent(name);
      if (
        (decodedName.startsWith('CognitoIdentityServiceProvider') ||
          decodedName.startsWith('amplify') ||
          decodedName.includes('selectedLob') ||
          decodedName.includes('selectedWarehouse') ||
          decodedName.includes('language')) &&
        !processedKeys.has(decodedName)
      ) {
        processedKeys.add(decodedName);

        // Read the value (reconstructing chunks if necessary)
        const safeKey = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        const match = document.cookie.match(
          new RegExp('(^| )' + safeKey + '=([^;]+)'),
        );

        if (match) {
          const val = match[2];
          if (val.startsWith('chunks:')) {
            const numChunks = parseInt(val.split(':')[1], 10);
            let fullValue = '';
            for (let j = 0; j < numChunks; j++) {
              const chunkMatch = document.cookie.match(
                new RegExp('(^| )' + safeKey + '\\.chunk' + j + '=([^;]+)'),
              );
              if (chunkMatch) {
                fullValue += chunkMatch[2];
                // Delete chunk cookie
                document.cookie = `${name}.chunk${j}=;${domainStr}path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
              }
            }
            localStorage.setItem(decodedName, decodeURIComponent(fullValue));
          } else {
            localStorage.setItem(decodedName, decodeURIComponent(val));
          }
        }

        // Delete base cookie
        document.cookie = `${name}=;${domainStr}path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      }
    }
  }
}
